"use client";

/* eslint-disable @next/next/no-img-element -- static Figma vectors at their exact intrinsic sizes */

import React, { useEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";
import { PageTrademania2Schema } from "@/sanity/pages/PageTrademania2Template";
import {
  Chip,
  Highlight,
  RevealWords,
  pad,
  prefersReducedMotion,
  revealLines,
  splitLastWord,
} from "@/components/trademania-2/shared";

import needle from "@/public/trademania-2/gauge-needle.svg";

const ANGLES = [-5, 54, 112, 171];
// share of the viewport height where an item counts as "being read"
const READING_LINE = 0.52;

// Figma px -> rem (1rem = 8px at 1920)
const r = (px: number) => `${px / 8}rem`;

// ring ticks (Figma ring artwork): 80 marks, 4.5deg apart, 34px long, inside the 730px ring
const TICK_COUNT = 80;
const TICK_STEP = 360 / TICK_COUNT;
const TICK_LENGTH = 34;
// the needle tip sits ~9deg clockwise of the needle rotation (Figma state lights the 0/4.5/9deg ticks)
const TIP_OFFSET = 9;
const TICK_OFF = "rgba(0,0,0,.14)";
const TICK_ON = "#9BE055";

/** Number of ticks (from 12 o'clock, clockwise) the needle tip has swept past at `angle`. */
const litTicks = (angle: number) => {
  const tip = angle + TIP_OFFSET;
  return tip < 0 ? 0 : Math.min(TICK_COUNT, Math.floor((tip + 1) / TICK_STEP) + 1);
};

/** 730px speedometer (Figma). Both gauges share the artwork; only half of each is on screen. */
function Gauge({
  needleRef,
  ticksRef,
  className,
}: {
  needleRef: React.Ref<HTMLDivElement>;
  ticksRef: React.Ref<SVGSVGElement>;
  className?: string;
}) {
  const lit = litTicks(0);
  return (
    <div aria-hidden className={cn("pointer-events-none absolute h-[91.25rem] w-[91.25rem]", className)}>
      <svg ref={ticksRef} viewBox="0 0 730 730" className="absolute inset-0 h-full w-full overflow-visible">
        {Array.from({ length: TICK_COUNT }, (_, i) => (
          <line
            key={i}
            x1={365}
            y1={0}
            x2={365}
            y2={TICK_LENGTH}
            stroke={i < lit ? TICK_ON : TICK_OFF}
            strokeWidth={2.5}
            transform={`rotate(${i * TICK_STEP} 365 365)`}
            className="transition-[stroke] duration-300 ease-out"
          />
        ))}
      </svg>
      <div ref={needleRef} className="absolute inset-0 will-change-transform">
        <img
          src={needle.src}
          alt=""
          width={37.712}
          height={295.415}
          className="absolute max-w-none"
          style={{ left: r(374.82), top: r(81.96), width: r(37.712), height: r(295.415) }}
        />
      </div>
    </div>
  );
}

export default function Why({ data }: { data: PageTrademania2Schema }) {
  const items = data?.whyItems ?? [];
  const [active, setActive] = useState(1);
  const listRef = useRef<HTMLOListElement>(null);
  const leftRef = useRef<HTMLDivElement>(null);
  const rightRef = useRef<HTMLDivElement>(null);
  const leftTicksRef = useRef<SVGSVGElement>(null);
  const rightTicksRef = useRef<SVGSVGElement>(null);

  // Scroll drives everything: the item crossing the reading line is the active one, and the
  // needles sweep continuously between the item angles (nothing is pinned or timed).
  useEffect(() => {
    const list = listRef.current;
    if (!list || prefersReducedMotion()) return;
    let raf = 0;
    let current = 0;
    let last = -1;
    let lastLit = litTicks(0);
    const loop = () => {
      raf = requestAnimationFrame(loop);
      const rows = Array.from(list.children) as HTMLElement[];
      if (!rows.length) return;
      const line = window.innerHeight * READING_LINE;
      const centres = rows.map((row) => {
        const r = row.getBoundingClientRect();
        return r.top + r.height / 2;
      });

      // fractional index of the reading line among the item centres
      let f = 0;
      if (line <= centres[0]) f = 0;
      else if (line >= centres[centres.length - 1]) f = centres.length - 1;
      else {
        const i = centres.findIndex((c, k) => line >= c && line < centres[k + 1]);
        f = i + (line - centres[i]) / (centres[i + 1] - centres[i]);
      }

      const index = Math.round(f);
      if (index !== last) {
        last = index;
        setActive(index);
      }

      const progress = Math.min(ANGLES.length - 1, (f / Math.max(1, items.length - 1.5)) * (ANGLES.length - 1));
      const lo = Math.floor(progress);
      const hi = Math.min(ANGLES.length - 1, lo + 1);
      const target = ANGLES[lo] + (ANGLES[hi] - ANGLES[lo]) * (progress - lo);
      current += (target - current) * 0.1;
      if (leftRef.current) leftRef.current.style.transform = `rotate(${current.toFixed(2)}deg)`;
      if (rightRef.current) rightRef.current.style.transform = `rotate(${current.toFixed(2)}deg)`;

      // every tick the needle has swept past turns lime, and greys out again when it sweeps back
      const lit = litTicks(current);
      if (lit !== lastLit) {
        lastLit = lit;
        for (const svg of [leftTicksRef.current, rightTicksRef.current]) {
          svg?.querySelectorAll("line").forEach((line, i) => line.setAttribute("stroke", i < lit ? TICK_ON : TICK_OFF));
        }
      }
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [items.length]);

  const [titleHead, titleLast] = splitLastWord(data?.whyTitle);

  return (
    <section
      className="relative overflow-hidden px-[2rem] pb-[1.5rem] pt-[10rem] lg:px-0 lg:pb-[30.25rem] lg:pt-[21.875rem]"
    >
      {/* Figma: 730px rings, desktop only (hidden on phones); the left gauge sweeps clockwise, the right one is its mirror image */}
      <div className="absolute left-0 top-[45rem] hidden w-0 origin-top-left scale-[.411] opacity-70 lg:top-[42.875rem] lg:block lg:scale-100 lg:opacity-100">
        <Gauge needleRef={leftRef} ticksRef={leftTicksRef} className="left-[-79.1rem] top-0 lg:left-[-43.375rem]" />
      </div>
      <div className="absolute right-0 top-[45rem] hidden w-0 origin-top-right scale-[.411] opacity-70 lg:top-[42.875rem] lg:block lg:scale-100 lg:opacity-100">
        <Gauge needleRef={rightRef} ticksRef={rightTicksRef} className="right-[-79.1rem] top-0 -scale-x-100 lg:right-[-36rem]" />
      </div>

      <div className="relative mx-auto flex w-full max-w-[113.625rem] flex-col items-center gap-[2.5rem] text-center lg:gap-[4.125rem]">
        <Chip text={data?.whyChip} slashes />
        <h2
          ref={revealLines}
          data-reveal
          className="font-owners text-[4.5rem] font-medium uppercase leading-[1.055] tracking-[.01em] text-black lg:max-w-[76.125rem] lg:text-[7.5rem] lg:leading-[1.08] lg:tracking-[.02em] tm2-trim-lg">
          {/* phones: the last word joins the highlight, one upright box per line, set off by a
              double space (Figma) */}
          <span className="lg:hidden">
            <RevealWords text={titleHead} />
            &nbsp;{" "}
            <Highlight>
              <RevealWords text={titleLast} />
            </Highlight>{" "}
            {data?.whyTitleHighlight && (
              <Highlight>
                <RevealWords text={data.whyTitleHighlight} />
              </Highlight>
            )}
          </span>
          <span className="hidden lg:inline">
            <RevealWords text={data?.whyTitle} />{" "}
            {data?.whyTitleHighlight && (
              <Highlight>
                <RevealWords text={data.whyTitleHighlight} />
              </Highlight>
            )}
          </span>
        </h2>
      </div>

      {/* phones (Figma): ruled above the first item and below every item, 24px apart */}
      <ol
        ref={listRef}
        className="relative mx-auto mt-[4rem] flex w-full max-w-[92.875rem] flex-col gap-[3rem] border-t border-black/10 pt-[2.5rem] lg:mt-[16rem] lg:gap-0 lg:border-t-0 lg:pt-0"
      >
        {items.map((item, i) => (
          <li
            key={i}
            className={cn(
              "flex gap-[1.5rem] border-b border-black/10 pb-[3rem] transition-opacity duration-700 ease-[cubic-bezier(.22,1,.36,1)] lg:gap-[2rem] lg:border-black/[.06] lg:pb-0",
              i < items.length - 1 ? "lg:mb-[4.5rem] lg:pb-[4.5rem]" : "lg:border-b-0",
              i === active ? "opacity-100" : "opacity-10",
            )}
          >
            <span
              className={cn(
                "shrink-0 font-owners text-[3.25rem] font-medium leading-[3.5rem] tracking-[.01em] transition-colors duration-500 lg:text-[5rem] lg:leading-[1.1] lg:tracking-[.023em]",
                i === active ? "text-[#9BE055]" : "text-[#0B0C0D]",
              )}
            >
              {pad(i + 1)}.
            </span>
            {/* phones: Figma trims the title to its baseline and leaves 8px to the copy; untrimmed
                that is 8px less the .19em under the baseline */}
            <div className="flex min-w-0 flex-1 flex-col gap-[.375rem] lg:w-fit lg:min-w-[76.875rem] lg:flex-none lg:gap-[1.25rem]">
              <h3
                ref={revealLines}
                data-reveal
                className="font-owners text-[3.25rem] font-medium leading-[3.5rem] tracking-[.01em] text-[#0B0C0D] lg:whitespace-nowrap lg:text-[5rem] lg:leading-[1.1] lg:tracking-[.023em]">
                <RevealWords text={item?.title} />
              </h3>
              {item?.text && (
                // Figma: the copy is as wide as its title (615px minimum)
                <p className="font-geist text-[2.25rem] font-medium leading-[1.4] tracking-[-.02em] text-black/60 lg:w-0 lg:min-w-full lg:tracking-[-.03em]">
                  {item.text}
                </p>
              )}
            </div>
          </li>
        ))}
      </ol>
    </section>
  );
}
